/**
 * Blog post (Deutsch): telc B2 Schreiben Guide mit Aufbau, Bewertungskriterien,
 * Fünf-Absätze-Vorlage und vollständigem Musterbrief (Beschwerdebrief).
 * Gleiche Fakten wie TelcB2SchreibenGuidePost (englisch); registriert in
 * BlogPostPage (BLOG_CONTENT_MAP_DE) und src/data/germanBlog.js.
 * SEO-Ziel: "telc b2 schreiben", "struktur schreiben b2 telc", "beschwerdebrief b2".
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function TelcB2SchreibenGuidePostDe() {
  const lang = useLangParam();
  const link = 'font-bold text-black underline underline-offset-2 hover:opacity-80';

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>Vom Logami-Team. Von Deutschlehrkräften entwickelt und mit den offiziellen telc-Bewertungskriterien abgeglichen.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Im <strong>Schriftlichen Ausdruck</strong> der telc B2 Prüfung hast du genau 30 Minuten für einen
        formellen Brief, den die Prüfer in wenigen Minuten bewerten. Die meisten Prüflinge verlieren hier
        aus demselben Grund Punkte: Sie behandeln die Aufgabe wie einen Sprachtest, dabei ist sie vor allem
        ein <strong>Strukturtest</strong>. Dieser Leitfaden zeigt dir das Aufgabenformat, die Bewertungskriterien,
        eine Vorlage Absatz für Absatz und einen kompletten Musterbrief, den du in der Prüfung anpassen kannst.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Das Aufgabenformat: Was in den 30 Minuten passiert
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Du bekommst <strong>zwei Schreibaufgaben und wählst eine</strong>. In fast jeder Prüfung sind beide
        formelle Briefe in der Sie-Form: meistens ein <strong>Beschwerdebrief</strong> oder eine formelle
        Anfrage. Zu jeder Aufgabe gehören <strong>vier Leitpunkte</strong>, von denen du
        {' '}<strong>mindestens drei</strong> sauber bearbeiten musst. Schreibe etwa
        {' '}<strong>150 bis 200 Wörter</strong>: genug, um drei Punkte auszuführen, und wenig genug, um deine
        Fehlerquote im Griff zu behalten.
      </p>
      <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] mb-10">
        <h3 className="font-space text-lg font-bold text-black mb-3">Auf einen Blick</h3>
        <ul className="list-disc pl-5 text-base text-black/70 leading-relaxed space-y-2">
          <li>30 Minuten, ein formeller Brief, Sie-Form</li>
          <li>Eine von zwei Aufgaben wählen; mindestens 3 von 4 Leitpunkten bearbeiten</li>
          <li>Keine Adressen und kein Datum nötig; beginne direkt mit dem Betreff</li>
          <li>Bewertet werden Inhalt, kommunikative Gestaltung und formale Richtigkeit</li>
        </ul>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        So bewerten Prüfer deinen Brief
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Die telc-Prüfer bewerten den Schriftlichen Ausdruck nach drei Kriterien. Wenn du sie kennst, schreibst
        du anders:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-10">
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-2">1. Inhalt</h3>
          <p className="text-base text-black/70 leading-relaxed">
            Hast du die Leitpunkte wirklich bearbeitet? Drei Punkte mit je zwei bis drei Sätzen zählen mehr
            als vier Punkte, die nur kurz erwähnt werden.
          </p>
        </div>
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-2">2. Kommunikative Gestaltung</h3>
          <p className="text-base text-black/70 leading-relaxed">
            Register, Aufbau und Konnektoren. Ein formeller Brief braucht einen Betreff, eine richtige Anrede,
            logisch verbundene Absätze und eine Schlussformel.
          </p>
        </div>
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-2">3. Formale Richtigkeit</h3>
          <p className="text-base text-black/70 leading-relaxed">
            Grammatik und Rechtschreibung. Auf B2-Niveau erwarten die Prüfer Nebensätze, Passiv und
            Konjunktiv II, richtig verwendet und nicht in jedem Satz.
          </p>
        </div>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Die Fünf-Absätze-Vorlage, die immer funktioniert
      </h2>
      <ol className="list-decimal pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li><strong>Betreff:</strong> eine sachliche Zeile. <em>Beschwerde über die Lieferung vom 12. Mai</em></li>
        <li><strong>Anrede:</strong> <em>Sehr geehrte Damen und Herren,</em> oder mit Namen, wenn die Aufgabe einen nennt</li>
        <li><strong>Einleitung (1 bis 2 Sätze):</strong> warum du schreibst, mit den wichtigsten Fakten (was, wann)</li>
        <li><strong>Hauptteil (3 kurze Absätze):</strong> ein Leitpunkt pro Absatz, jeweils mit einem Konnektor oder Redemittel eröffnet</li>
        <li><strong>Schluss + Grußformel:</strong> eine klare Forderung oder Bitte mit Frist, dann <em>Mit freundlichen Grüßen</em> und dein Name</li>
      </ol>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Musterbrief: Beschwerdebrief (Aufgabentyp aus echten Prüfungen)
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Aufgabe: Du hast online einen Laptop bestellt. Er kam zu spät und beschädigt an, und der Kundenservice
        hat nicht geantwortet. Beschwere dich und fordere eine Lösung.
      </p>
      <div className="p-5 sm:p-6 rounded-2xl border-2 border-black bg-white mb-6 font-medium text-black/80 leading-relaxed text-base">
        <p className="mb-4"><strong>Betreff: Beschwerde über meine Bestellung Nr. 48213</strong></p>
        <p className="mb-4">Sehr geehrte Damen und Herren,</p>
        <p className="mb-4">
          am 3. Juli habe ich in Ihrem Onlineshop einen Laptop bestellt, der laut Bestellbestätigung
          innerhalb von fünf Werktagen geliefert werden sollte. Leider muss ich mich nun aus mehreren
          Gründen bei Ihnen beschweren.
        </p>
        <p className="mb-4">
          Zunächst einmal kam das Gerät erst nach drei Wochen bei mir an, obwohl ich es dringend für
          meine Arbeit benötigt hätte. Darüber hinaus musste ich beim Auspacken feststellen, dass das
          Display beschädigt ist, sodass sich der Laptop nicht verwenden lässt. Besonders ärgerlich finde
          ich jedoch, dass Ihr Kundenservice auf meine beiden E-Mails bis heute nicht reagiert hat.
        </p>
        <p className="mb-4">
          Ich fordere Sie daher auf, mir bis zum 15. August ein einwandfreies Ersatzgerät zu liefern oder
          den vollen Kaufpreis zu erstatten. Andernfalls sehe ich mich gezwungen, rechtliche Schritte
          einzuleiten.
        </p>
        <p className="mb-1">Mit freundlichen Grüßen</p>
        <p>Anna Petrova</p>
      </div>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Das macht dieser Brief richtig: drei Leitpunkte in drei Absätzen, jeweils mit einem Konnektor
        eröffnet (<em>zunächst einmal, darüber hinaus, besonders ärgerlich finde ich</em>), B2-Grammatik an
        den richtigen Stellen (<em>hätte benötigt, sodass, sehe ich mich gezwungen</em>) und eine konkrete
        Forderung mit Frist. Mehr braucht ein Prüfer nicht, um gute Punkte zu geben.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Die Fehler, die die meisten Punkte kosten
      </h2>
      <ul className="list-disc pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li><strong>Betreff oder Grußformel fehlt.</strong> Das führt sofort zu Abzügen bei der kommunikativen Gestaltung.</li>
        <li><strong>Falsches Register.</strong> Ein einziges „du“ im formellen Brief zeigt das falsche Niveau.</li>
        <li><strong>300 Wörter schreiben.</strong> Mehr Text bedeutet mehr Fehler, aber keine zusätzlichen Inhaltspunkte.</li>
        <li><strong>Die Aufgabe nacherzählen.</strong> Führe deine Punkte aus, statt die Aufgabe zu umschreiben.</li>
        <li><strong>Keine Zeit zum Korrigieren.</strong> Plane am Ende 5 Minuten ein, um Verbendungen, Kasus und Kommas zu prüfen.</li>
      </ul>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        So trainierst du das in zwei Wochen
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Schreibe jeden zweiten Tag einen Brief unter Prüfungsbedingungen: 30 Minuten, ohne Wörterbuch. Lerne
        die{' '}<Link to={`/${lang}/blog/telc-b2-redemittel-schreiben`} className={link}>telc B2 Redemittel</Link>{' '}
        (Erklärungen auf Englisch), bis die Formulierungen automatisch kommen, und lass dann jeden Übungsbrief
        bewerten. Im{' '}<Link to={`/${lang}/mock-exams/telc-b2`} className={link}>Logami telc B2 Modelltest</Link>{' '}
        wird dein Brief nach denselben drei Kriterien bewertet, du siehst also nach jedem Versuch deine Werte
        für Inhalt, Aufbau und Richtigkeit getrennt.
      </p>

      <RelatedResources provider="telc" level="b2" variant="exam-guide" />
    </article>
  );
}
